import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  RefreshCw,
  Plus,
  Package,
  ArrowRightLeft,
  UserPlus,
  Settings,
  Activity,
} from 'lucide-react';

export type ActivityType =
  | 'REFILL_COMPLETED'
  | 'MACHINE_CREATED'
  | 'STOCK_RECEIVED'
  | 'TRANSFER_COMPLETED'
  | 'USER_CREATED'
  | 'SETTINGS_CHANGED';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  message: string;
  userId: string | null;
  userName: string | null;
  createdAt: string;
}

interface ActivityFeedProps {
  activities: ActivityItem[];
  isLoading?: boolean;
  className?: string;
  maxItems?: number;
}

const activityConfig: Record<ActivityType, { icon: typeof Activity; color: string }> = {
  REFILL_COMPLETED: { icon: RefreshCw, color: 'text-green-600 bg-green-100' },
  MACHINE_CREATED: { icon: Plus, color: 'text-blue-600 bg-blue-100' },
  STOCK_RECEIVED: { icon: Package, color: 'text-purple-600 bg-purple-100' },
  TRANSFER_COMPLETED: { icon: ArrowRightLeft, color: 'text-orange-600 bg-orange-100' },
  USER_CREATED: { icon: UserPlus, color: 'text-indigo-600 bg-indigo-100' },
  SETTINGS_CHANGED: { icon: Settings, color: 'text-gray-600 bg-gray-100' },
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

export function ActivityFeed({ activities, isLoading = false, className, maxItems = 10 }: ActivityFeedProps) {
  const displayedActivities = activities.slice(0, maxItems);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 bg-muted animate-pulse rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
                <div className="h-3 bg-muted animate-pulse rounded w-1/4" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Activity className="h-5 w-5 mr-2" />
            <span>No recent activity</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-muted" />
          <div className="space-y-4">
            {displayedActivities.map((activity, index) => {
              const config = activityConfig[activity.type] ?? activityConfig.SETTINGS_CHANGED;
              const Icon = config.icon;

              return (
                <div key={activity.id} className="relative flex items-start gap-3 pl-1">
                  <div className={cn(
                    'relative z-10 flex h-8 w-8 items-center justify-center rounded-full shrink-0',
                    config.color
                  )}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <p className="text-sm font-medium truncate">{activity.message}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      {activity.userName && (
                        <>
                          <span>{activity.userName}</span>
                          <span>·</span>
                        </>
                      )}
                      <span>{formatRelativeTime(activity.createdAt)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default ActivityFeed;
